<template>
  <v-container fluid class="px-12 py-4" v-if="loadingComplete">
    <billing-and-roi-builder></billing-and-roi-builder>
    <v-row>
      <v-col cols="6">
        <breadcrumbs></breadcrumbs>
        <h1 data-cy="buildStructureTitle">List - Proposed Structure</h1>
      </v-col>
      <v-col cols="6" class="text-right">
        <div class="mt-5">
          <v-btn
            @click="$router.push({ name: 'structureChart' })"
            color="primary"
            text
            ><v-icon small class="mr-2">fas fa-project-diagram</v-icon
            ><span data-cy="toggleView">Chart View</span></v-btn
          >
        </div>
      </v-col>
    </v-row>
    <v-divider class="mb-4"></v-divider>
    <v-row>
      <v-col cols="8">
        <h2 class="title" data-cy="organizationLabel">
          {{ organization.name }} (SA{{ organization.serviceAreaId }})
        </h2>
        <div class="body-2" v-if="organization.abbreviation">
          Abbreviation: {{ organization.abbreviation }}
        </div>
      </v-col>
      <v-col class="text-right" cols="4"
        ><v-btn
          :disabled="isDisabled"
          @click="$router.push({ name: 'structureOrganizationForm' })"
          data-cy="organizationEditButton"
          class="mt-3"
          color="primary"
          outlined
          v-if="canEditOrganization"
        >
          Edit
        </v-btn></v-col
      >
    </v-row>
    <v-col>
      <div v-if="canEditOrganization">
        <lock-structure-checkbox
          @structureLockToggled="reloadData"
        ></lock-structure-checkbox>
      </div>
    </v-col>
    <v-divider class="my-5"></v-divider>
    <v-row>
      <v-col cols="6">
        <h2 class="title">Locations</h2>
      </v-col>
      <v-col cols="6">
        <div class="text-right">
          <v-btn
            :disabled="isDisabled"
            @click="navigateToCreate"
            color="primary"
            data-cy="addLocation"
            outlined
            v-if="locationPermission.create"
          >
            Add
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <v-card outlined class="mt-3">
      <v-card-text class="py-1">
        <v-data-table
          :headers="locationHeaders"
          :items="locationListForDisplay"
          outlined
          data-cy="table"
        >
          <template v-slot:item.id="{ item }">{{
            item.locationNumber
          }}</template>
          <template v-slot:item.name="{ item }">
            <router-link :to="`/build/structure/location/${item.locationId}`">{{
              item.name
            }}</router-link>
          </template>
          <template v-slot:item.abbreviation="{ item }">{{
            `${item.abbreviation}`.toUpperCase()
          }}</template>
          <template v-slot:item.departments="{ item }">
            <router-link
              :to="`/build/structure/location/${item.locationId}/department/list`"
              >{{
                item.departmentList ? item.departmentList.length : 0
              }}</router-link
            >
          </template>
          <template v-slot:item.created="{ item }">{{
            formatTimestamp(item.created)
          }}</template>
          <template v-slot:item.createdBy="{ item }">{{
            (getUserById(item.createdBy) || {}).email
          }}</template>
          <template v-slot:item.actions="{ item }">
            <DeleteFkEntityDialog
              :data-cy="`deleteBtn--location${item.locationId}-noDepartments`"
              :disabled="deleteLocationIsDisabled(item.locationId)"
              :fkEntityId="item.locationId"
              :fkEntityLabel="item.name"
              fkEntityName="location"
              v-if="!locationHasDepartments(item.locationId)"
            ></DeleteFkEntityDialog>
            <v-tooltip top v-else>
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <v-btn
                    :data-cy="`deleteBtn--location${item.locationId}`"
                    :disabled="deleteLocationIsDisabled(item.locationId)"
                    outlined
                    small
                    >Delete</v-btn
                  >
                </div>
              </template>
              <span>
                Cannot delete location when it has departments, the structure is
                locked, or the build is complete.
              </span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
  <generic-loader v-else> </generic-loader>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { formatTimestamp } from "@/lib/componentUtil";

import Breadcrumbs from "@/views/Breadcrumbs";
import DeleteFkEntityDialog from "@/components/DeleteFkEntityDialog";
import GenericLoader from "@/components/GenericLoader";

import BillingAndRoiBuilder from "./BillingAndRoiBuilder";
import LockStructureCheckbox from "./LockStructureCheckbox";

export default {
  components: {
    BillingAndRoiBuilder,
    Breadcrumbs,
    DeleteFkEntityDialog,
    GenericLoader,
    LockStructureCheckbox,
  },
  computed: {
    buildIsClosed() {
      return this.organizationStatusIsGreaterThanOrEqualTo("buildClosed");
    },
    isDisabled() {
      if (this.buildIsClosed) {
        return this.buildIsClosed;
      }

      return this.structureIsLocked;
    },
    locationListForDisplay() {
      return this.locationList.map(location => {
        const { locationId } = location;

        location.departmentList = this.getDepartmentListByLocationId(
          "locationId",
          locationId
        );

        return location;
      });
    },

    structureIsLocked() {
      return this.getStructureStatus;
    },
    ...mapGetters({
      getUserById: "user/getById",
      getDepartmentListByLocationId: "department/filterByAttributeValue",
      getStructureStatus: "organizationStatus/getStructureStatus",
      organizationStatusIsGreaterThanOrEqualTo:
        "organizationStatus/organizationStatusIsGreaterThanOrEqualTo",
    }),
    ...mapState({
      canEditOrganization: state =>
        state.session.user.permission.structureApp.updateOrganization,
      departmentList: state => state.department.list,
      locationList: state => state.location.list,
      locationPermission: state => state.session.user.permission.location,
      organization: state => state.organization.record,
      organizationId: state => state.session.user.organizationId,
    }),
  },
  async created() {
    this.loadingComplete = false;
    await this.reloadData();
    this.loadingComplete = true;
  },
  data() {
    return {
      departmentHeaders: [
        {
          text: "Name",
          align: "left",
          sortable: true,
          value: "name",
          width: "30%",
        },
      ],
      expanded: [],
      loadingComplete: false,
      locationHeaders: [
        {
          text: "ID",
          align: "left",
          sortable: true,
          value: "id",
        },
        {
          text: "Name",
          align: "left",
          sortable: true,
          value: "name",
        },
        {
          text: "Abbreviation",
          align: "left",
          sortable: false,
          value: "abbreviation",
        },
        {
          text: "Number of Departments",
          align: "left",
          sortable: true,
          value: "departments",
        },
        {
          text: "Created on",
          align: "left",
          sortable: true,
          value: "created",
        },
        {
          text: "Created by",
          align: "left",
          sortable: true,
          value: "createdBy",
        },
        {
          text: "",
          align: "center",
          value: "actions",
        },
      ],
    };
  },
  methods: {
    deleteLocationIsDisabled(locationId) {
      if (this.isDisabled) {
        return this.isDisabled;
      }
      if (this.locationHasDepartments(locationId)) {
        return true;
      }
      return false;
    },
    locationHasDepartments(locationId) {
      const departmentList = this.getDepartmentListByLocationId(
        "locationId",
        locationId
      );

      return departmentList.length > 0;
    },
    navigateToCreate() {
      this.$router.push({ name: "structureLocationCreate" });
    },
    async reloadData() {
      try {
        await Promise.all([
          this.getDepartmentList({ reload: false }),
          this.getLocationList({ reload: false }),
          this.getOrganization(this.organizationId),
          this.getUserList({ reload: false }),
        ]);

        this.locationList.forEach(location => {
          const { locationId } = location;
          location.departmentList = this.getDepartmentListByLocationId(
            "locationId",
            locationId
          );
        });
      } catch (e) {
        console.error(e);
      }
    },
    ...mapActions({
      getDepartmentList: "department/list",
      getLocationList: "location/list",
      getOrganization: "organization/getByOrganizationId",
      getUserList: "user/list",
    }),
    formatTimestamp,
  },
};
</script>
